import { useRef, useState, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { InputText } from 'primereact/inputtext';
import { useNavigate } from 'react-router-dom';
import logo from "../../assets/logos/TRANSKYRIOS-_IconColor.avif";
import "./mecanica.css";
import { historicoVehicular } from '../../services/mecanicaService.js'; 
import HistorialVehiculoTable from '../../components/table/historial-vehiculo-table.js';
import { exportarPDF } from '../../utils/pdf/pdfDataTable.js';
import { exportarExcel } from '../../utils/exportarExcel.js'
import useMedia from '../../hooks/media-querys'
import { formatFecha } from '../../utils/dateFormatter.js';

function HistoricoVehicular() {
  const isPhone  = useMedia('(max-width: 640px)');
  const labelPdf   = isPhone ? 'PDF'   : 'Exportar PDF';
  const labelExcel = isPhone ? 'Excel' : 'Exportar Excel';
  const fixWidth = isPhone ? "140px" : "auto";
  const fixSize = isPhone ? "0.75rem" : "1rem";

  const dtRef = useRef(null); 
  const [visibleRows, setVisibleRows] = useState([]);
  const [patente, setPatente] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useRef(null);
  const navigate = useNavigate();

  const normalizarFila = useCallback((r) => {
    // Asegura que existan todas las claves que usa la tabla.
    return {
      numeroOT: r?.numeroOT ?? r?.ot ?? r?.numero_ot ?? null,
      mecanico_id: r?.mecanico_id ?? null,
      mecanico_nombre: r?.mecanico_nombre ?? r?.mecanico ?? null,
      mecanico2_nombre: r?.mecanico2_nombre ?? null,
      servicio_tipo: r?.servicio_tipo ?? r?.tipoServicio ?? null,
      fechaIngreso: r?.fechaIngreso ?? r?.fecha_ingreso ?? r?.ingreso ?? null,
      fechaSalida: r?.fechaSalida ?? r?.fecha_salida ?? r?.salida ?? null,
      marca: r?.marca ?? null,
      modelo: r?.modelo ?? null,
      anio: r?.anio ?? r?.año ?? null,
      nmotor: r?.nmotor ?? r?.n_motor ?? null,
      nserie: r?.nserie ?? r?.n_serie ?? null,
      kilometrajeAct: r?.kilometrajeAct ?? r?.km_actual ?? r?.kilometraje_actual ?? null,
      kilometrajeProx: r?.kilometrajeProx ?? r?.km_proximo ?? r?.kilometraje_proximo ?? null,
      combustible: r?.combustible ?? null,
      sintomas: r?.sintomas ?? null,
      observaciones: r?.observaciones ?? null,
      recomendacion: r?.recomendacion ?? null,
      diagnostico: r?.diagnostico ?? null,
      obsMecanico: r?.obsMecanico ?? r?.obs_mecanico ?? null,
      repuestos: r?.repuestos ?? null,        // puede ser string o array
      ultimaReparacion: r?.ultimaReparacion ?? r?.ultima_reparacion ?? null,
      evaluaciones: r?.evaluaciones ?? null,  // puede ser string o array
      cliente_nombre: r?.cliente_nombre ?? r?.cliente?.nombre ?? null,
      cliente_rut: r?.cliente_rut ?? r?.cliente?.rut ?? null,
      cliente_correo: r?.cliente_correo ?? r?.cliente?.correo ?? null,
      cliente_celular: r?.cliente_celular ?? r?.cliente?.celular ?? null,
    };
  }, []);

  const handleBuscar = useCallback(async () => {
    const p = patente.trim();
    if (!p) {
      toast.current?.show({ 
        severity: "warn", summary: "Patente vacía", 
        detail: "Ingresa una patente para buscar." 
      });
      return;
    }
    setLoading(true);
    try {

      const resp = await historicoVehicular(p);

      if (resp.status === 403) {
        toast.current?.show({ 
          severity: "error", summary: "Permisos insuficientes", 
          detail: resp.message || "No tiene permisos suficientes para realizar busquedas."
        });
        setData([]);
        return;
      }

      const filas = Array.isArray(resp) ? resp : (resp?.data ?? resp?.items ?? []);
      setData(filas.map(normalizarFila));
      
      if (!filas.length) {
        toast.current?.show({ 
          severity: "info", summary: "Sin resultados", 
          detail: "No hay registros para esa patente." 
        });
      }

    } catch (error) {
      toast.current?.show({
        severity: "error", 
        summary: "Error", 
        detail: error || "Error inesperado al consultar el histórico vehicular." });
    } finally {
      setLoading(false);
    }
  }, [patente, normalizarFila]);

  // Enter para buscar
  const onKeyDown = (e) => { if (e.key === "Enter") handleBuscar(); };

  // Convierte arrays a texto amigable
  const repuestosToText = (arr) =>
    Array.isArray(arr) && arr.length
      ? arr.map(r => `${r?.cantidad ?? ''} ${r?.unidad ?? ''} ${r?.nombre ?? ''}`.trim())
          .filter(Boolean).join(' | ')
      : '';

  const evaluacionesToText = (arr) =>
    Array.isArray(arr) && arr.length
      ? arr.map(e => `${e?.nombre ?? ''}: ${e?.estado ?? ''}`.trim())
          .filter(Boolean).join(' | ')
      : '';

  const columnasExcel = [
    // --- columnas visibles en la UI ---
    { header: 'OT',                key: 'OT',                width: 10, get: r => r.numeroOT ?? '' },
    { header: 'Servicio',          key: 'Servicio',          width: 22, get: r => r.servicio_tipo ?? '' },
    { header: 'Síntomas',          key: 'Sintomas',          width: 34, get: r => r.sintomas ?? '' },
    { header: 'Diagnóstico',       key: 'Diagnostico',       width: 34, get: r => r.diagnostico ?? '' },
    { header: 'Trabajo Realizado', key: 'TrabajoRealizado',  width: 34, get: r => r.recomendacion ?? '' },
    { header: 'Repuestos',         key: 'Repuestos',         width: 34, get: r => repuestosToText(r.repuestos) },
    { header: 'Evaluaciones',      key: 'Evaluaciones',      width: 34, get: r => evaluacionesToText(r.evaluaciones) },
    { header: 'Ingreso',           key: 'Ingreso',           width: 16, get: r => formatFecha(r.fechaIngreso) ?? '' },
    { header: 'Salida',            key: 'Salida',            width: 16, get: r => formatFecha(r.fechaSalida) ?? '' },

    // --- columnas extra (no visibles en la tabla) ---
    { header: 'Mecánico 1',        key: 'Mecanico1',         width: 22, get: r => r.mecanico_nombre ?? '' },
    { header: 'Mecánico 2',        key: 'Mecanico2',         width: 22, get: r => r.mecanico2_nombre ?? '' },
    { header: 'Km Actual',         key: 'KmActual',          width: 14, get: r => r.kilometrajeAct ?? '' },
    { header: 'Km Próximo',        key: 'KmProximo',         width: 14, get: r => r.kilometrajeProx ?? '' },
    { header: 'Combustible',       key: 'Combustible',       width: 14, get: r => r.combustible ?? '' },
    { header: 'Observaciones',     key: 'Observaciones',     width: 34, get: r => r.observaciones ?? '' },
    { header: 'Obs. Mecánico',     key: 'ObsMecanico',       width: 34, get: r => r.obsMecanico ?? '' },
    { header: 'Última Reparación', key: 'UltimaReparacion',  width: 20, get: r => r.ultimaReparacion ?? '' },
    { header: 'Cliente Nombre',    key: 'ClienteNombre',     width: 24, get: r => r.cliente_nombre ?? '' },
    { header: 'Cliente RUT',       key: 'ClienteRUT',        width: 18, get: r => (r.cliente_rut ?? '').toString() },
    { header: 'Cliente Correo',    key: 'ClienteCorreo',     width: 28, get: r => r.cliente_correo ?? '' },
    { header: 'Cliente Celular',   key: 'ClienteCelular',    width: 20, get: r => (r.cliente_celular ?? '').toString() },
  ];

  const handleExportarExcel = () => {
    const rowsParaExportar = visibleRows.length ? visibleRows : data;
    exportarExcel({
      rows: rowsParaExportar,
      columns: columnasExcel,
      filename: `historial_vehicular_${(patente || '').trim()}.xlsx`
    });
  };

  // Toolbar 

  // Buscar por patente
  const startToolbar = () => {
    const HeadingTag = isPhone ? "h5" : "h4";
    return (
      <div className="buscador-container">
        <HeadingTag className="m-0">Ingrese una Patente: </HeadingTag>
        <div className='flex gap-2 align-items-center justify-content-end'>
          <InputText 
            type='text'
            className='input-text-m'
            style={{ color:'#1d4ed8', fontSize: 16, fontWeight: 'bold'}} 
            placeholder="Ej. ABCD12"
            value={patente}
            onChange={(e) => setPatente(e.target.value.toUpperCase())}
            onKeyDown={onKeyDown} 
          />
          <Button 
            icon="pi pi-search"
            label={isPhone ? null : "Buscar"} 
            className="inv-btn-add"
            onClick={handleBuscar}
            loading={loading}
          />
        </div>
      </div>
    );
  };

  // Exportar a
  const endToolbar = () => {
    const nombreArchivo = 'historial_vehicular';
    const tituloArchivo = `HISTÓRICO: VEHÍCULO "${patente || ''}"`;
    const subTituloArchivo = 'TALLER MECÁNICO';
    const rowsParaExportar = visibleRows.length ? visibleRows : data;

    return(
      <div className='toolbar-group'>
        <Button 
          className="btn-pdf" 
          label={labelPdf}
          style={{ fontSize: fixSize, width: fixWidth }}
          icon="pi pi-download"
          disabled={data.length === 0}
          onClick={() => 
            exportarPDF({dtRef: dtRef, data: rowsParaExportar, nombreArchivo, tituloArchivo, subTituloArchivo}
          )}
        />
        <Button 
          className="btn-excel" 
          label={labelExcel}
          style={{ fontSize: fixSize, width: fixWidth}}
          icon="pi pi-download" 
          disabled={data.length === 0}
          onClick={handleExportarExcel}
        />
      </div> 
    )
  };

  const volverAtras = () => { navigate('/app/taller');};

  return (
    <div className="tmc-container">
      <Toast ref={toast} position="center"/>
  
      <header className="inv-header pb-2">
        <div className='flex gap-3'>
          <Button type="button" label="" icon="pi pi-arrow-left" className="btn-volver-login" onClick={volverAtras} />
          <h2 className='mt-1 mb-0'>Taller Mecánico</h2>
        </div>
        <img src={logo} alt="Transkyrios" className="inv-logo" />
      </header>

      <div className="table-container card">
        <Toolbar className="inv-toolbar" end={endToolbar} start={startToolbar}></Toolbar>
        <HistorialVehiculoTable 
          dtRef={dtRef}
          data={data}
          onVisibleChange={setVisibleRows}
        />
      </div>
    </div>
  );
}

export default HistoricoVehicular;